import { FC } from 'react';
import { User, Calendar } from 'lucide-react';
import { type ScheduledCall } from '@teachur/http-client';
import { cn } from '@new/lib';
import Modal from '@new/common/ui/Modal/Modal';
import { InfoRow } from '../shared/InfoRow';
import { ScheduledCallActions } from './ScheduledCallActions';
import {
  getScheduledCallStatusText,
  getScheduledCallStatusColor,
  formatTime,
} from '@new/features/dashboard/lib/helpers';
import { formatDateLong } from '../../lib/helpers';
import { timestampToDate } from '@new/common/lib/dateTime';

interface ScheduledCallDetailsModalProps {
  scheduledCall: ScheduledCall;
  isOpen: boolean;
  onClose: () => void;
  onJoinCall?: (videoCallId: string) => void;
}

export const ScheduledCallDetailsModal: FC<ScheduledCallDetailsModalProps> = ({
  scheduledCall,
  isOpen,
  onClose,
  onJoinCall,
}) => {
  const callDate = timestampToDate(scheduledCall.startTime);
  const dateStr = formatDateLong(callDate);

  const title = scheduledCall.type === 'onboarding' ? 'Onboarding Call' : 'Course Hearing';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-4">
        <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
          <span className="text-sm font-medium text-neutral-700">Status</span>
          <span
            className={cn(
              'px-3 py-1 rounded-md text-xs font-medium border',
              getScheduledCallStatusColor(scheduledCall.status)
            )}
          >
            {getScheduledCallStatusText(scheduledCall.status)}
          </span>
        </div>

        <div className="space-y-3">
          <InfoRow icon={Calendar} label={dateStr} value={formatTime(scheduledCall.startTime)} />

          {scheduledCall.student && (
            <InfoRow
              icon={User}
              label="Student"
              value={`${scheduledCall.student.firstName} ${scheduledCall.student.lastName}`}
            />
          )}

          {scheduledCall.instructor && (
            <InfoRow
              icon={User}
              label="Instructor"
              value={`${scheduledCall.instructor.firstName} ${scheduledCall.instructor.lastName}`}
            />
          )}
        </div>
      </div>

      <ScheduledCallActions
        scheduledCall={scheduledCall}
        onClose={onClose}
        onJoinCall={onJoinCall}
      />
    </Modal>
  );
};
